import { Location } from '@angular/common';
import { DashboardService } from './../../services/dashboard.service';
import { Component, OnDestroy, OnInit, ViewChild, TemplateRef, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { appConfig, PRIVACY_OPTIONS, PRIVACY_DEFAULTS } from 'src/app/_core/config/app-config.const';
import { AuthService } from 'src/app/_services/auth.service';
import { CommonService } from 'src/app/_services/common.service';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'edit-tangram',
  templateUrl: './edit-tangram.component.html',
  styleUrls: ['./edit-tangram.component.scss']
})
export class EditTangramComponent implements OnInit, OnDestroy {
  @ViewChild('PasswordModalContent', { static: true }) passwordModalContent!: TemplateRef<any>;
  @ViewChild('InviteModalContent', { static: true }) inviteModalContent!: TemplateRef<any>;
  @ViewChild('alertModalContent', { static: true }) alertModalContent!: TemplateRef<any>;
  @ViewChild('PublishModalContent', { static: true }) publishModalContent!: TemplateRef<any>;

  appConfig = appConfig;
  iconPath = appConfig.iconPath;
  imgPath = appConfig.imagePath;
  setupPathwayForm: FormGroup;
  profileImageUrl: string = "";
  currentUser: any;
  currentPathwayId = '';
  tanTitle: any = '';
  isLoaded: boolean = false;
  tanDesc: any = '';
  tanDescLength = 0;
  initialDescLength = 0;

  // Loading states
  isLoading: boolean = true;
  isUpdating: boolean = false;
  isPublishing: boolean = false;
  isCreator: boolean = false;

  // Draft state + publishable preview
  isDraft: boolean = false;

  // Preview state — mirrors my-tangram-dashboard so we reuse the same view components.
  isDashboard: boolean = true;
  overviewImageUrl: string = '';
  playlists: any[] = [];
  currentVideoDetail: any = { _id: '' };
  trackVideoDetail: any = {};
  videoId: string = '';
  pathwayDetail: any = { title: '', description: '' };
  curatorInfo: any = { _id: null, firstName: '', lastName: '' };
  ratingCounts: any = {};
  userRatings: any = {};
  videosDuration: any = { totalVideo: '', totalVideoMinutes: '' };
  contentProcessed: any = { data: 0 };

  // Privacy Settings
  privacySettings: any = null;
  selectedPrivacyOption: string = PRIVACY_DEFAULTS.PRIVACY_OPTION;
  isPasswordSet: boolean = false;
  tangramPassword: string = '';
  confirmPassword: string = '';
  passwordError: string = '';
  alterMessage: string = '';
  alertCancelText: string = '';
  alertSuccessText: string = '';
  alertType: string = ''; // 'validation' | 'removePassword' | 'removeInvite' | 'publish'

  // Invite Only Settings
  invitedEmails: any[] = [];
  newInviteEmail: string = '';
  newInvitedEmails: string[] = [];
  inviteMessage: string = '';
  inviteError: string = '';

  // Remove confirmation
  emailToRemove: any = null;
  emailToRemoveIndex: number = -1;

  // Publish flow (draft → published) — captures privacy at publish time.
  publishPrivacyOption: string = PRIVACY_DEFAULTS.PRIVACY_OPTION;
  publishPasswordSet: boolean = false;
  publishInvitedEmails: any[] = [];
  publishError: string = '';

  constructor(
    private location: Location,
    private commonService: CommonService,
    private authService: AuthService,
    private dashboardService: DashboardService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private toasterService: ToastrService,
    private modalService: NgbModal,
    private cd: ChangeDetectorRef,
    ) {
    this.currentUser = this.authService.currentUserValue;
  }

  ngOnInit() {
    this.activatedRoute.params.subscribe((param: any) => {
      this.currentPathwayId = param.pathwayId;
    });
    this.getPathway(this.currentPathwayId);
  }

  ngOnDestroy(): void {
    this.commonService.changeView('');
  }

  backButton() {
    this.location.back();
  }

  /**
   * Load full pathway (metadata + media) so the edit view can reuse the same
   * components as the published dashboard — nothing gets rendered as a half
   * view or missing a preview.
   */
  getPathway(pathwayId: any) {
    this.isLoading = true;
    this.dashboardService.getPathwayDetail(pathwayId).subscribe((res: any) => {
      const d = (res && res.data) || {};
      const {
        title = '',
        description = '',
        privacyOption,
        isPasswordProtected,
        isPrivate,
        isInviteOnly,
        isDraft,
        createdBy,
        videos = [],
        admin,
        imageURL,
        totalVideos,
        totalDurationMIN,
        ratingCounts,
        totalVideoWatchPercentage,
      } = d;

      this.isLoaded = true;
      this.isDraft = !!isDraft;
      this.tanTitle = title || '';
      this.tanDesc = description || '';
      this.tanDescLength = (description || '').length;
      this.initialDescLength = this.tanDescLength;
      this.isCreator = createdBy === this.currentUser._id;

      // Bind preview data the same way my-tangram-dashboard does.
      this.pathwayDetail = { title, description };
      this.curatorInfo = admin || this.curatorInfo;
      this.playlists = Array.isArray(videos) ? videos : [];
      this.ratingCounts = ratingCounts || {};
      this.videosDuration = {
        totalVideo: totalVideos || (this.playlists ? this.playlists.length : 0),
        totalVideoMinutes: totalDurationMIN || 0,
      };
      this.contentProcessed = { data: totalVideoWatchPercentage || 0 };
      // Thumbnail priority: pathway-level imageURL, else the first media's
      // thumbnail. No static fallback — the template simply hides the <img>
      // when overviewImageUrl is empty.
      this.overviewImageUrl = this.resolveTangramThumbnail(imageURL, this.playlists);

      // Privacy settings
      this.privacySettings = {
        privacyOption: privacyOption || PRIVACY_DEFAULTS.PRIVACY_OPTION,
        isPasswordProtected: isPasswordProtected || false,
        isPrivate: isPrivate || false,
        isInviteOnly: isInviteOnly || false
      };
      this.selectedPrivacyOption = this.privacySettings.privacyOption;
      this.isPasswordSet = this.privacySettings.isPasswordProtected;

      if (this.selectedPrivacyOption === PRIVACY_OPTIONS.INVITE_ONLY) {
        this.loadInvitedEmails();
      }

      this.isLoading = false;
    }, (error) => {
      this.isLoading = false;
      this.toasterService.error('Failed to load tangram details');
    });
  }

  // ── Preview helpers — reuse the exact flow my-tangram-dashboard uses. ──

  /** Triggered when a user clicks a media item in the playlist / thumbnail slider. */
  onPlaylistClick(video: any) {
    if (!video || !video._id) return;
    this.isDashboard = false;
    this.currentVideoDetail = video;
    this.videoId = video._id;
    this.loadVideoDetail(video._id);
    this.pathwayDetail = {
      title: video.title,
      description: video.description,
      tagName: video.tag && video.tag.name,
      videoURL: video.videoURL,
    };
  }

  /** Switch back to overview (closes the video detail view). */
  onDashboardClick() {
    this.isDashboard = true;
    this.currentVideoDetail = { _id: '' };
    this.trackVideoDetail = {};
    // Reset pathway-detail to top-level metadata.
    this.pathwayDetail = { title: this.tanTitle, description: this.tanDesc };
  }

  /** Used by the video player overlay when a video ends. */
  loadNextVideo() {
    if (!this.playlists || !this.playlists.length) return;
    const idx = this.playlists.findIndex((p) => p._id === this.videoId);
    if (idx !== -1 && idx < this.playlists.length - 1) {
      this.onPlaylistClick(this.playlists[idx + 1]);
    }
  }

  /** Kept for binding parity with the view page — watch progress is non-destructive. */
  onUpdateConsumedContent(data: any) {
    this.dashboardService.trackVideoWatch(data).subscribe((res: any) => {
      const percentage = res && res.data && res.data.percentage;
      if (typeof percentage === 'number') {
        this.contentProcessed = { data: percentage };
      }
    });
  }

  private loadVideoDetail(videoId: string) {
    this.dashboardService.getVideoDetail(videoId).subscribe((data: any) => {
      if (!data || !data.data) return;
      const { ratingCounts, isLiked, isDisliked, isFavourite, spotlights, tag } = data.data;
      this.ratingCounts = ratingCounts || {};
      this.userRatings = { isLiked, isDisliked, isFavourite };
      this.currentVideoDetail = { ...this.currentVideoDetail, tag, spotlights };
      this.cd.detectChanges();
    });
    this.dashboardService.getTrackedVideoWatch(videoId).subscribe((res: any) => {
      this.trackVideoDetail = (res && res.data) || {};
    });
  }

  updateOverviewImageUrl() {
    // On broken image, try the first media's thumbnail. If that also fails
    // or doesn't exist, blank the URL so the template hides the <img>.
    const firstMediaThumb = this.firstMediaThumbnail(this.playlists);
    this.overviewImageUrl = firstMediaThumb && firstMediaThumb !== this.overviewImageUrl
      ? firstMediaThumb
      : '';
  }

  /**
   * Pick the tangram thumbnail. Prefer the pathway's own imageURL, otherwise
   * fall back to the first media's thumbnail. Never returns a static asset —
   * empty string when neither is available.
   */
  private resolveTangramThumbnail(imageURL: string | undefined, playlists: any[]): string {
    if (imageURL) return `${environment.backendUrl}${imageURL}`;
    return this.firstMediaThumbnail(playlists) || '';
  }

  private firstMediaThumbnail(playlists: any[]): string {
    if (!Array.isArray(playlists)) return '';
    for (const media of playlists) {
      const thumb = media && media.thumbnail;
      if (!thumb) continue;
      const url = thumb.mediumURL || thumb.highURL || thumb.smallURL;
      if (url) return url;
    }
    return '';
  }

  // ── Navigation helpers ──

  /** Jump into the create-tangram wizard in "add media to existing pathway" mode. */
  addMedia() {
    this.router.navigate(['/create-tangram'], { queryParams: { pathwayId: this.currentPathwayId } });
  }

  /** Route to the dedicated video-edit view (reuses create-tangram in edit mode). */
  editMedia(video: any) {
    if (!video || !video._id) return;
    this.router.navigate(['/edit-tangram-video', video._id]);
  }

  /**
   * Calculate length of description
   */
  calculateDescLength(event: any) {
    this.tanDescLength = (event.target.value || '').length;
  }

  /**
   * Update Tangram Details
   */
  updateTangram() {
    if (this.selectedPrivacyOption === PRIVACY_OPTIONS.PASSWORD_PROTECTED && !this.isPasswordSet) {
      this.alterMessage = 'Password Protected option requires a password to be set. Please set a password first.';
      this.alertCancelText = '';
      this.alertSuccessText = 'OK';
      this.alertType = 'validation';
      this.modalService.open(this.alertModalContent, { centered: true, windowClass: 'unlock-premium' });
      return;
    }

    if (this.selectedPrivacyOption === PRIVACY_OPTIONS.INVITE_ONLY) {
      const hasExistingEmails = this.invitedEmails && this.invitedEmails.length > 0;
      const hasNewEmails = this.newInvitedEmails && this.newInvitedEmails.length > 0;
      if (!hasExistingEmails && !hasNewEmails) {
        this.alterMessage = 'Invite Only option requires at least one email to be invited. Please add email addresses first.';
        this.alertCancelText = '';
        this.alertSuccessText = 'OK';
        this.alertType = 'validation';
        this.modalService.open(this.alertModalContent, { centered: true, windowClass: 'unlock-premium' });
        return;
      }
    }

    this.isUpdating = true;
    const data = {
      pathwayId: this.currentPathwayId,
      title: this.tanTitle,
      description: this.tanDesc,
      privacyOption: this.selectedPrivacyOption,
      isPasswordProtected: this.selectedPrivacyOption === PRIVACY_OPTIONS.PASSWORD_PROTECTED,
      isPrivate: this.selectedPrivacyOption === PRIVACY_OPTIONS.PRIVATE,
      isInviteOnly: this.selectedPrivacyOption === PRIVACY_OPTIONS.INVITE_ONLY,
    };

    this.dashboardService.updateTangramDetails(data).subscribe(() => {
      this.isUpdating = false;
      // Keep the user on the edit page if still a draft so they can keep iterating.
      if (this.isDraft) {
        this.pathwayDetail = { title: this.tanTitle, description: this.tanDesc };
        this.toasterService.success('Draft saved');
      } else {
        this.router.navigate(['/dashboard', this.currentPathwayId]);
      }
    }, () => {
      this.isUpdating = false;
      this.toasterService.error('Failed to update Tangram', 'Error');
    });
  }

  /**
   * Publish a draft tangram — opens a modal that lets the user pick privacy
   * (public / private / password / invite-only) before flipping isDraft.
   * Requires at least one media item.
   */
  publishDraft() {
    if (!this.playlists || this.playlists.length === 0) {
      this.alterMessage = 'Add at least one media item before publishing.';
      this.alertCancelText = '';
      this.alertSuccessText = 'OK';
      this.alertType = 'validation';
      this.modalService.open(this.alertModalContent, { centered: true, windowClass: 'unlock-premium' });
      return;
    }
    // Seed the publish modal with whatever privacy state already exists on the
    // pathway (drafts default to PUBLIC on the backend).
    this.publishPrivacyOption = this.selectedPrivacyOption || PRIVACY_DEFAULTS.PRIVACY_OPTION;
    this.publishPasswordSet = this.isPasswordSet;
    this.publishInvitedEmails = [...(this.invitedEmails || [])];
    this.publishError = '';
    this.modalService.open(this.publishModalContent, { centered: true, size: 'lg', windowClass: 'publish-draft-modal' });
  }

  onPublishPrivacyOptionSelect(optionId: string) {
    this.publishPrivacyOption = optionId;
    if (optionId === PRIVACY_OPTIONS.INVITE_ONLY) {
      this.loadInvitedEmails();
    }
  }

  onPublishPasswordSet(event: { pathwayId: string, password: string }) {
    this.dashboardService.updateTangramPassword(event.pathwayId, event.password)
      .subscribe((response: any) => {
        if (response.success) {
          this.publishPasswordSet = true;
          this.isPasswordSet = true;
        } else {
          this.toasterService.error('Failed to set password', 'Error');
        }
      }, () => this.toasterService.error('Failed to set password', 'Error'));
  }

  onPublishPasswordRemoved(pathwayId: string) {
    this.dashboardService.removeTangramPassword(pathwayId)
      .subscribe((response: any) => {
        if (response.success) {
          this.publishPasswordSet = false;
          this.isPasswordSet = false;
          this.publishPrivacyOption = PRIVACY_OPTIONS.PUBLIC;
        }
      });
  }

  /**
   * Commit the publish: save the chosen privacy first, then flip isDraft.
   */
  confirmPublishDraft(modal: any) {
    this.publishError = '';
    // Validate publish-time privacy requirements.
    if (this.publishPrivacyOption === PRIVACY_OPTIONS.PASSWORD_PROTECTED && !this.publishPasswordSet) {
      this.publishError = 'Set a password before publishing with password protection.';
      return;
    }
    if (this.publishPrivacyOption === PRIVACY_OPTIONS.INVITE_ONLY) {
      const hasInvites = (this.publishInvitedEmails && this.publishInvitedEmails.length > 0)
        || (this.invitedEmails && this.invitedEmails.length > 0);
      if (!hasInvites) {
        this.publishError = 'Add at least one invited email before publishing.';
        return;
      }
    }

    this.isPublishing = true;

    const privacyPayload = {
      pathwayId: this.currentPathwayId,
      title: this.tanTitle,
      description: this.tanDesc,
      privacyOption: this.publishPrivacyOption,
      isPasswordProtected: this.publishPrivacyOption === PRIVACY_OPTIONS.PASSWORD_PROTECTED,
      isPrivate: this.publishPrivacyOption === PRIVACY_OPTIONS.PRIVATE,
      isInviteOnly: this.publishPrivacyOption === PRIVACY_OPTIONS.INVITE_ONLY,
    };

    this.dashboardService.updateTangramDetails(privacyPayload).subscribe(() => {
      // Sync local state so UI reflects the saved privacy immediately.
      this.selectedPrivacyOption = this.publishPrivacyOption;
      this.privacySettings = {
        privacyOption: this.publishPrivacyOption,
        isPasswordProtected: privacyPayload.isPasswordProtected,
        isPrivate: privacyPayload.isPrivate,
        isInviteOnly: privacyPayload.isInviteOnly,
      };
      // Now publish.
      this.dashboardService.publishDraft(this.currentPathwayId).subscribe((res: any) => {
        this.isPublishing = false;
        if (res && res.success) {
          this.isDraft = false;
          modal && modal.close('Published');
          this.toasterService.success('Draft published!');
          this.router.navigate(['/dashboard', this.currentPathwayId]);
        } else {
          this.toasterService.error((res && res.message) || 'Failed to publish draft.');
        }
      }, (err: any) => {
        this.isPublishing = false;
        this.toasterService.error((err && err.error && err.error.message) || 'Failed to publish draft.');
      });
    }, (err: any) => {
      this.isPublishing = false;
      this.toasterService.error((err && err.error && err.error.message) || 'Failed to save privacy settings.');
    });
  }

  onPrivacyOptionSelect(optionId: string) {
    this.selectedPrivacyOption = optionId;
    if (optionId === PRIVACY_OPTIONS.INVITE_ONLY) {
      this.loadInvitedEmails();
    }
  }

  onPasswordSet(event: { pathwayId: string, password: string }) {
    this.dashboardService.updateTangramPassword(event.pathwayId, event.password)
      .subscribe((response: any) => {
        if (response.success) {
          this.isPasswordSet = true;
        } else {
          this.toasterService.error('Failed to set password', 'Error');
        }
      }, () => this.toasterService.error('Failed to set password', 'Error'));
  }

  onPasswordChanged(event: { pathwayId: string, password: string }) {
    this.dashboardService.updateTangramPassword(event.pathwayId, event.password)
      .subscribe((response: any) => {
        if (response.success) {
          this.isPasswordSet = true;
        } else {
          this.toasterService.error('Failed to change password', 'Error');
        }
      }, () => this.toasterService.error('Failed to change password', 'Error'));
  }

  onPasswordRemoved(pathwayId: string) {
    this.dashboardService.removeTangramPassword(pathwayId)
      .subscribe((response: any) => {
        if (response.success) {
          this.isPasswordSet = false;
          this.selectedPrivacyOption = PRIVACY_OPTIONS.PUBLIC;
        } else {
          this.toasterService.error('Failed to remove password', 'Error');
        }
      }, () => this.toasterService.error('Failed to remove password', 'Error'));
  }

  setPassword(modal: any) {
    this.passwordError = '';
    if (!this.tangramPassword) {
      this.passwordError = 'Please enter a password';
      return;
    }
    if (this.tangramPassword.length < 6) {
      this.passwordError = 'Password must be at least 6 characters long';
      return;
    }
    if (this.tangramPassword !== this.confirmPassword) {
      this.passwordError = 'Passwords do not match';
      return;
    }
    this.dashboardService.updateTangramPassword(this.currentPathwayId, this.tangramPassword)
      .subscribe((response: any) => {
        if (response.success) {
          this.isPasswordSet = true;
          modal.close('Password Set');
        } else {
          this.passwordError = response.message || 'Failed to set password';
        }
      }, () => this.passwordError = 'Failed to set password');
  }

  removePassword() {
    this.alterMessage = 'Are you sure you want to remove the password protection from this Tangram?';
    this.alertCancelText = 'No';
    this.alertSuccessText = 'Yes, Remove';
    this.alertType = 'removePassword';
    this.modalService.open(this.alertModalContent, { centered: true, windowClass: 'unlock-premium' });
  }

  confirmRemovePassword() {
    this.dashboardService.removeTangramPassword(this.currentPathwayId)
      .subscribe((response: any) => {
        if (response.success) {
          this.isPasswordSet = false;
          this.selectedPrivacyOption = PRIVACY_OPTIONS.PUBLIC;
        }
      }, () => this.toasterService.error('Failed to remove password', 'Error'));
  }

  cancelRemovePassword() {}

  onAlertClose() {}

  onAlertSuccess() {
    if (this.alertType === 'removePassword') {
      this.confirmRemovePassword();
    } else if (this.alertType === 'removeInvite') {
      this.confirmRemoveInvite();
    } else if (this.alertType === 'validation') {
      this.onAlertClose();
    }
  }

  onAlertCancel() {
    if (this.alertType === 'removePassword') {
      this.cancelRemovePassword();
    } else if (this.alertType === 'removeInvite') {
      this.cancelRemoveInvite();
    } else if (this.alertType === 'validation') {
      this.onAlertClose();
    }
  }

  openInviteModal() {
    this.inviteError = '';
    this.newInviteEmail = '';
    this.newInvitedEmails = [];
    this.inviteMessage = '';
    this.loadInvitedEmails();
    this.modalService.open(this.inviteModalContent, { centered: true, size: 'lg' });
  }

  addInviteEmail() {
    if (this.newInviteEmail && this.newInviteEmail.trim()) {
      const email = this.newInviteEmail.trim().toLowerCase();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.inviteError = 'Please enter a valid email address';
        return;
      }
      if (this.newInvitedEmails.includes(email)) {
        this.inviteError = 'This email is already in the list';
        return;
      }
      this.newInvitedEmails.push(email);
      this.newInviteEmail = '';
      this.inviteError = '';
    }
  }

  removeNewInviteEmail(index: number) {
    this.newInvitedEmails.splice(index, 1);
  }

  onInviteModalClosed(event: any, modal: any) {
    if (event && event.action === 'removeConfirmation') {
      this.emailToRemove = event.email;
      this.emailToRemoveIndex = event.index;
      this.alterMessage = `Do you want to remove access for ${event.email}?`;
      this.alertCancelText = 'No';
      this.alertSuccessText = 'Yes, Remove';
      this.alertType = 'removeInvite';
      this.modalService.open(this.alertModalContent, { centered: true, windowClass: 'unlock-premium' });
    } else {
      modal.close('Cancel');
    }
  }

  onInvitesSent(inviteData: any, modal?: any) {
    if (inviteData.isGoogleStyleLayout) {
      this.updateAccessTypes(inviteData, modal);
    } else {
      this.sendInvitations(inviteData, modal);
    }
  }

  confirmRemoveInvite() {
    if (this.emailToRemove && this.emailToRemoveIndex >= 0) {
      this.dashboardService.removeInvitedEmail(this.currentPathwayId, this.emailToRemove).subscribe(
        (response: any) => {
          if (response.success) {
            this.invitedEmails.splice(this.emailToRemoveIndex, 1);
            this.toasterService.success('Access removed successfully');
            this.modalService.dismissAll();
          } else {
            this.toasterService.error('Failed to remove access');
            this.modalService.dismissAll();
          }
        },
        () => {
          this.toasterService.error('Failed to remove access');
          this.modalService.dismissAll();
        }
      );
    } else {
      this.modalService.dismissAll();
    }
  }

  cancelRemoveInvite() {
    this.modalService.dismissAll();
  }

  private sendInvitations(inviteData: any, modal: any) {
    this.dashboardService.sendInviteOnlyInvitations(this.currentPathwayId, inviteData.emails, inviteData.message)
      .subscribe((response: any) => {
        if (response.success) {
          this.toasterService.success(`Invitations sent to ${inviteData.emails.length} people successfully!`);
          modal && modal.close('Invitations Sent');
          this.loadInvitedEmails();
        } else {
          this.toasterService.error(response.message || 'Failed to send invitations');
        }
      }, (error: any) => {
        this.toasterService.error((error.error && error.error.message) || 'Error sending invitations');
      });
  }

  private updateAccessTypes(inviteData: any, modal: any) {
    const emailUpdates = inviteData.emails.map((emailObj: any) => ({
      email: emailObj.email,
      accessType: emailObj.accessType
    }));
    this.dashboardService.updateInvitedAccess(this.currentPathwayId, emailUpdates)
      .subscribe((response: any) => {
        if (response.success) {
          this.toasterService.success(`Access types updated for ${response.data.updated} people successfully!`);
          modal && modal.close('Access Updated');
          this.loadInvitedEmails();
        } else {
          this.toasterService.error(response.message || 'Failed to update access types');
        }
      }, (error: any) => {
        this.toasterService.error((error.error && error.error.message) || 'Error updating access types');
      });
  }

  loadInvitedEmails() {
    this.dashboardService.getInvitedEmails(this.currentPathwayId)
      .subscribe((response: any) => {
        if (response.success) {
          this.invitedEmails = response.data;
        } else {
          this.toasterService.error('Failed to load invited emails');
        }
      }, () => this.toasterService.error('Error loading invited emails'));
  }

  removeInvitedEmail(index: number) {
    const emailToRemove = this.invitedEmails[index].email;
    this.dashboardService.removeInvitedEmail(this.currentPathwayId, emailToRemove)
      .subscribe((response: any) => {
        if (response.success) {
          this.toasterService.success('Email removed from invitations');
          this.loadInvitedEmails();
        } else {
          this.toasterService.error(response.message || 'Failed to remove email');
        }
      }, () => this.toasterService.error('Error removing email'));
  }
}
